import type { Mbti } from '@tarot/shared';

/**
 * 방 좌석 재접속용 기억 — 새로고침·앱 전환으로 돌아왔을 때 닉네임을 다시 묻지 않기 위한 것.
 *
 * sessionStorage를 쓰는 이유:
 *  - 탭을 유지한 채 새로고침하면 살아남고, 탭을 닫으면 사라진다. 딱 필요한 수명이다
 *  - 회원 설정이 아니라 이 탭에서 진행 중인 방의 임시 상태라, DB 저장 원칙과 무관하다
 *
 * 좌석을 실제로 돌려주는 판단은 서버(RoomDO)가 한다. 여기 값은 "누구로 다시 들어갈지"의 힌트일 뿐,
 * 이 값이 있다고 남의 좌석을 뺏을 수는 없다 (연결이 살아 있는 닉네임은 서버가 거절한다).
 */

const KEY = 'tw_room_seat';

interface SavedSeat {
  roomId: string;
  nickname: string;
  mbti?: Mbti;
  /**
   * 좌석 소유 증명 — 서버가 seat:assigned로 발급한다.
   * 재접속 때 함께 보내면 "닉네임이 이미 사용 중"으로 막히지 않고 원래 자리를 돌려받는다.
   * 개인정보가 아니라 이 방의 임시 좌석 열쇠다(방이 정리되면 서버에서도 사라진다).
   */
  token?: string;
}

export function rememberSeat(roomId: string, nickname: string, mbti?: Mbti, token?: string): void {
  try {
    const saved: SavedSeat = {
      roomId,
      nickname,
      ...(mbti ? { mbti } : {}),
      ...(token ? { token } : {}),
    };
    sessionStorage.setItem(KEY, JSON.stringify(saved));
  } catch {
    /* 저장 불가(프라이빗 모드 등) — 재접속 시 닉네임을 다시 물을 뿐 기능은 그대로 */
  }
}

/** 같은 방의 기록만 돌려준다 — 다른 방으로 이동했다면 무시 */
export function recallSeat(roomId: string): SavedSeat | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as SavedSeat;
    return saved.roomId === roomId && saved.nickname ? saved : null;
  } catch {
    return null;
  }
}

/** 방을 완전히 떠날 때 정리 — 남겨두면 다음에 엉뚱하게 재입장한다 */
export function forgetSeat(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* 무시 */
  }
}
